<script setup lang="ts">
/**
 * The Danoffice IT square — the brand's primary graphic element.
 *
 * From the brand guidelines: "the main graphic element of our brand identity
 * is a square. It is a basic way to add brand recognition to any of our
 * designs. It can be used as a definitive standalone shape or to create
 * decorative elements, simple patterns and tile compositions."
 *
 * The nested form (a smaller square centred inside a larger one, 1x within
 * 2x) is the composition the guidelines draw. Reproduced with two elements
 * and brand colours rather than an image, so it inherits the palette, scales
 * to any size, and costs nothing to load.
 *
 * Deliberately not the logo: this is a texture mark for empty states, section
 * eyebrows and loading indicators. Where the actual logo belongs, use the
 * logo.
 */
withDefaults(
  defineProps<{
    /** Outer edge length. Any CSS length. */
    size?: string;
    /** `solid` fills the outer square with the primary and insets the
     *  highlight — the guidelines' own pairing. `outline` keeps the outer as
     *  a hairline, for places where a filled block would shout. */
    variant?: "solid" | "outline";
  }>(),
  { size: "1rem", variant: "solid" },
);
</script>

<template>
  <span
    class="brand-square"
    :class="`brand-square--${variant}`"
    :style="{ '--sq': size }"
    aria-hidden="true"
  >
    <span class="brand-square__inner"></span>
  </span>
</template>

<style scoped>
.brand-square {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--sq);
  block-size: var(--sq);
  flex: 0 0 auto;
  /* Square, not rounded. The shape IS the brand element — rounding it to
     match the surrounding pills would delete the thing being expressed. */
  border-radius: 0;
}
.brand-square--solid {
  background: var(--color-accent);
}
.brand-square--outline {
  background: transparent;
  box-shadow: inset 0 0 0 1px var(--color-accent);
}
/* 1x inside 2x — the inner square is half the outer edge, centred. */
.brand-square__inner {
  inline-size: 50%;
  block-size: 50%;
  background: var(--color-highlight);
  border-radius: 0;
}
.brand-square--outline .brand-square__inner {
  background: var(--color-highlight);
}
</style>
